import React from 'react';

import AudioInputList from './AudioInputList/AudioInputList';
import AudioOutputList from './AudioOutputList/AudioOutputList';
import {
  DialogContent,
  Typography,
  Divider,
  Dialog,
  DialogActions,
  Button,
  DialogTitle,
  Box,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VideoInputList from './VideoInputList/VideoInputList';
import MaxGalleryViewParticipants from './MaxGalleryViewParticipants/MaxGalleryViewParticipants';
import { useAppState } from '../../state';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    [theme.breakpoints.down('xs')]: {
      margin: '16px',
    },
  },
}));

const Container = styled(DialogContent)(({ theme }) => ({
  width: '600px',
  minHeight: '400px',
  [theme.breakpoints.down('xs')]: {
    width: 'calc(100vw - 32px)',
  },
  '& .inputSelect': {
    width: 'calc(100% - 35px)',
  },
}));

const Headline = styled(Typography)({
  marginBottom: '1.3em',
  fontSize: '1.1rem',
});

const ListSection = styled('div')({
  margin: '2em 0 0.8em',
  '&:first-of-type': {
    margin: '1em 0 2em 0',
  },
});

const StyledButton = styled(Button)({
  float: 'right',
});

const NoiseCancellationSection = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.5em 0',
});

export default function DeviceSelectionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { settings, dispatchSetting } = useAppState();
  const noiseCancellationEnabled = settings?.noiseCancellationEnabled ?? true;

  const handleNoiseCancellationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchSetting({ name: 'noiseCancellationEnabled', value: event.target.checked });
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle>Audio and Video Settings</DialogTitle>
      <Divider />
      <Container>
        <ListSection>
          <Headline variant="h6">
            Video
          </Headline>
          <VideoInputList />
        </ListSection>
        <Divider />
        <ListSection>
          <Headline variant="h6">
            Audio
          </Headline>
          <AudioInputList />
        </ListSection>
        <ListSection>
          <AudioOutputList />
        </ListSection>
        <Divider />
        <ListSection>
          <Headline variant="h6">
            AI Noise Cancellation
          </Headline>
          <NoiseCancellationSection>
            <Tooltip 
              title="Uses Krisp AI to remove background noise from your microphone. May increase CPU usage."
              arrow
            >
              <Typography variant="body2" color="text.secondary">
                Remove background noise with AI
              </Typography>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={noiseCancellationEnabled}
                  onChange={handleNoiseCancellationToggle}
                  color="primary"
                />
              }
              label={noiseCancellationEnabled ? 'On' : 'Off'}
              labelPlacement="start"
            />
          </NoiseCancellationSection>
          <Typography variant="caption" color="text.secondary">
            Powered by Krisp AI. Works best in Chrome and Firefox.
          </Typography>
        </ListSection>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Divider />
          <ListSection>
            <Headline variant="h6">
              Gallery View
            </Headline>
            <MaxGalleryViewParticipants />
          </ListSection>
        </Box>
      </Container>
      <Divider />
      <DialogActions>
        <StyledButton color="primary" variant="contained" onClick={onClose}>
          Done
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
}

